// STEP STUDIO-OVERLAY-DRAG-RESIZE-1 · EgressTemplate 의 QR · 사이즈표 렌더를 공통 컴포넌트로 추출.
//   · Egress 출력은 그대로 (vw/vh 단위 유지) · Studio 미리보기는 무대 크기 기준 단위(vw/vh 함수)로 동일 렌더.
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';

const _vw = (n) => `${n}vw`;
const _vh = (n) => `${n}vh`;

// STEP OVERLAY-2J-QR · Pure sub-component — client-side QR rendering.
// qrcode library (ShareModal 이 이미 사용) 재사용 · 외부 QR SaaS 없음.
// Error correction level 'M' · 영상 압축/외부 재송출 안정성 최소 보장.
export const QrImage = ({ url, size }) => {
  const [dataUrl, setDataUrl] = useState('');
  useEffect(() => {
    if (!url) { setDataUrl(''); return; }
    QRCode.toDataURL(url, { margin: 1, width: size || 150,
                              errorCorrectionLevel: 'M' })
      .then(setDataUrl)
      .catch(() => setDataUrl(''));
  }, [url, size]);
  if (!dataUrl) {
    return <div style={{width: size, height: size,
                         display: 'flex', alignItems: 'center',
                         justifyContent: 'center',
                         fontSize: 10, color: '#999'}}
                data-testid="egress-overlay-qr-loading">QR</div>;
  }
  return <img src={dataUrl} alt="QR" width={size} height={size}
               data-testid="egress-overlay-qr-image" />;
};

export const OverlayQrCard = ({ url, size, isPortrait, label = '상품 보러가기' }) => (
  <div style={{
         display: 'flex', flexDirection: 'column', alignItems: 'center',
         gap: 6,
         padding: '10px 10px 8px',
         background: '#fff',
         border: '2px solid #FF4F3D',
         borderRadius: 12,
         boxShadow: '0 10px 32px rgba(0,0,0,0.35)',
       }}
       data-testid="egress-overlay-qr-card">
    <QrImage url={url} size={size} />
    <div style={{
           fontSize: isPortrait ? 13 : 11, fontWeight: 900,
           color: '#0A0A0A', letterSpacing: 0.4,
         }}
         data-testid="egress-overlay-qr-label">{label}</div>
  </div>
);

export const sizeTableHasData = (sizeTable) => {
  if (!sizeTable || typeof sizeTable !== 'object') return false;
  const cols = Array.isArray(sizeTable.columns) ? sizeTable.columns : [];
  const rows = Array.isArray(sizeTable.rows) ? sizeTable.rows : [];
  const guide = (sizeTable.guide_text || '').toString().trim();
  return !!(cols.length || rows.length || guide || sizeTableDesc(sizeTable).length);
};

// STUDIO-OVERLAY-UI-CONSOLIDATION-1 · 상품설명 소재·기타 (입력한 항목만).
// LIVE-NOTICE-DESCRIPTION-UI-FIX-1 · 공지·상품설명·타이머 공통 연한 반투명 팔레트 (Studio·Egress·소비자 공용).
export const LIGHT = {
  bg: 'rgba(255,255,255,0.74)', bgPaused: 'rgba(232,232,236,0.82)', border: '1px solid rgba(255,255,255,0.7)',
  text: '#17181C', sub: '#4A4B50', accent: '#E5352B', line: 'rgba(0,0,0,0.12)', lineSoft: 'rgba(0,0,0,0.06)',
  shadow: '0 8px 24px rgba(0,0,0,0.18)',
};

export const sizeTableDesc = (sizeTable) => [['소재', sizeTable?.material], ['기타', sizeTable?.etc]]
  .map(([k, v]) => [k, (v || '').toString().trim()]).filter(([, v]) => v);

export const OverlaySizeTableCard = ({ sizeTable, isPortrait, vw = _vw, vh = _vh, free = false }) => {
  const cols = Array.isArray(sizeTable?.columns) ? sizeTable.columns : [];
  const rows = Array.isArray(sizeTable?.rows) ? sizeTable.rows : [];
  const guide = (sizeTable?.guide_text || '').toString().trim();
  return (
    <div style={{
           display: 'flex', flexDirection: 'column', gap: 8,
           padding: isPortrait ? '14px 14px' : '14px 18px',
           background: LIGHT.bg,
           border: LIGHT.border,
           borderRadius: 14,
           backdropFilter: 'blur(14px)',
           WebkitBackdropFilter: 'blur(14px)',
           color: LIGHT.text,
           maxWidth: '100%',
           maxHeight: free ? 'none' : (isPortrait ? vh(58) : vh(70)),
           overflow: 'hidden',
           boxShadow: LIGHT.shadow,
         }}
         data-testid="egress-overlay-size-table-card">
      <div style={{
             display: 'inline-flex', alignItems: 'center', gap: 6,
             fontSize: isPortrait ? 12 : 11, fontWeight: 900,
             letterSpacing: 0.4, color: LIGHT.accent,
           }}
           data-testid="egress-overlay-size-table-label">
        상품설명
      </div>
      {(cols.length > 0 && rows.length > 0) && (
        <div style={{ overflow: free ? 'visible' : 'auto', maxHeight: free ? 'none' : (isPortrait ? vh(38) : vh(50)) }}>
          <table style={{
                   width: '100%', borderCollapse: 'collapse',
                   fontSize: isPortrait ? vw(3.4) : 14,
                   lineHeight: 1.25,
                 }}
                 data-testid="egress-overlay-size-table-grid">
            <thead>
              <tr>
                {cols.map((c, ci) => (
                  <th key={ci}
                      style={{
                        padding: isPortrait ? '6px 4px' : '8px 10px',
                        borderBottom: `1px solid ${LIGHT.line}`,
                        fontWeight: 900, textAlign: 'center',
                        color: LIGHT.text, whiteSpace: 'nowrap',
                      }}>
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri}>
                  {cols.map((_, ci) => (
                    <td key={ci}
                        style={{
                          padding: isPortrait ? '5px 4px' : '7px 10px',
                          borderBottom: `1px solid ${LIGHT.lineSoft}`,
                          textAlign: 'center',
                          color: LIGHT.text,
                          fontWeight: ci === 0 ? 800 : 600,
                        }}>
                      {(r && r[ci]) || ''}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {sizeTableDesc(sizeTable).map(([k, v]) => (
        <div key={k} style={{ display: 'flex', gap: 10, fontSize: isPortrait ? vw(3.4) : 14, lineHeight: 1.35 }}
             data-testid={`egress-overlay-desc-${k === '소재' ? 'material' : 'etc'}`}>
          <span style={{ fontWeight: 900, color: LIGHT.accent, flexShrink: 0 }}>{k}</span>
          <span style={{ fontWeight: 700, whiteSpace: 'pre-line', wordBreak: 'keep-all' }}>{v}</span>
        </div>
      ))}
      {guide && (
        <div style={{
               fontSize: isPortrait ? 12 : 11, fontWeight: 700,
               lineHeight: 1.35, color: LIGHT.sub,
               borderTop: `1px solid ${LIGHT.line}`,
               paddingTop: 6, whiteSpace: 'pre-line',
             }}
             data-testid="egress-overlay-size-table-guide">
          {guide}
        </div>
      )}
    </div>
  );
};

// STEP STUDIO-OVERLAY-UX-FIX-1-STEP2 · Studio 미리보기 공지 카드 (EgressTemplate 공지 스타일 동일 · 기본 위치).
export const OverlayNoticeCard = ({ notice, isPortrait, showImage, free = false, testidPrefix = 'studio', stageW = 0, boxW = 0 }) => {
  const innerRef = useRef(null);
  const textRef = useRef(null);
  const [innerH, setInnerH] = useState(0);
  // STUDIO-NOTICE-TEXT-FIT-FINAL-1 · 실측 맞춤: { fs: 글자 px, clamp: 최소 크기에서도 넘칠 때 줄 수 }.
  const [fit, setFit] = useState(null);
  const text = (notice?.text || '').toString().trim();
  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') { return undefined; }
    const ro = new ResizeObserver(() => setInnerH(el.offsetHeight));
    ro.observe(el);
    setInnerH(el.offsetHeight);
    return () => ro.disconnect();
  });
  useLayoutEffect(() => {
    // 본문 영역 실제 폭 기준 · 높이 예산 = 폭×0.8 · 10~28px 이분 탐색 (복제 노드에서 측정 · 줄바꿈 포함).
    const t = textRef.current;
    if (!free || !t || !text) { return undefined; }
    const run = () => {
      const W = t.clientWidth;
      if (!W) return;
      const H = W * 0.8;
      const probe = t.cloneNode(true);
      Object.assign(probe.style, { position: 'absolute', visibility: 'hidden', left: '0', top: '0', width: `${W}px`,
        display: 'block', maxHeight: 'none', WebkitLineClamp: 'unset', height: 'auto' });
      t.parentNode.appendChild(probe);
      const fits = (f) => { probe.style.fontSize = `${f}px`; return probe.scrollHeight <= H && probe.scrollWidth <= W + 1; };
      let lo = 10, hi = 18;
      if (fits(hi)) lo = hi;
      else if (!fits(lo)) hi = lo;
      else { for (let i = 0; i < 8; i += 1) { const m = (lo + hi) / 2; if (fits(m)) lo = m; else hi = m; } }
      const fs = Math.floor(lo * 10) / 10;
      probe.style.fontSize = `${fs}px`;
      const clamp = probe.scrollHeight > H + 1 ? Math.max(1, Math.floor(H / (fs * 1.28))) : 0;
      probe.remove();
      setFit((p) => (p && p.fs === fs && p.clamp === clamp ? p : { fs, clamp }));
    };
    run();
    if (document.fonts?.ready) document.fonts.ready.then(run).catch(() => {});
    return undefined;
  }, [free, text, boxW, stageW, isPortrait]);
  if (!text && !(showImage && notice?.image_url)) return null;
  const sz = notice?.image_size || 'medium';
  const maxH = isPortrait ? (sz === 'small' ? 90 : sz === 'large' ? 240 : 150) : (sz === 'small' ? 70 : sz === 'large' ? 180 : 120);
  // STUDIO-NOTICE-TIMER-LAYOUT-FIX-1 · free = 배치 박스 안. (글자 수 추정 k 는 실측 전 첫 페인트 fallback 전용)
  const k = text.length <= 20 ? 7 : text.length <= 50 ? 5.6 : text.length <= 100 ? 4.6 : text.length <= 150 ? 4 : 3.6;
  const overflow = !!fit?.clamp;
  // 기준 무대 폭(9:16=390 · 16:9=1280)에서 레이아웃 후 zoom 으로 확대·축소 → 미리보기↔송출 해상도 무관 동일 줄바꿈 (WYSIWYG).
  const z = free && stageW > 0 && boxW > 0 ? stageW / (isPortrait ? 390 : 1280) : 0;
  const px = (n) => `${n}px`;
  const wrap = free
    ? { position: 'relative', width: '100%', display: 'flex', justifyContent: 'center', pointerEvents: 'none', ...(z ? {} : { containerType: 'inline-size' }) }
    : isPortrait
    ? { position: 'absolute', top: '13%', left: '5%', right: '5%', display: 'flex', justifyContent: 'center', pointerEvents: 'none', zIndex: 12 }
    : { position: 'absolute', top: '5%', left: '50%', transform: 'translateX(-50%)', maxWidth: '60%', pointerEvents: 'none', zIndex: 12 };
  return (
    <div style={wrap} data-testid={`${testidPrefix}-overlay-notice-wrap`} data-free={free ? '1' : '0'}>
      {/* transform scale (zoom 은 글꼴 크기를 곱해 해상도별 줄바꿈이 달라짐) · 바깥 div 높이 = 측정 높이 × z. */}
      <div style={z ? { width: `${boxW}px`, height: innerH ? `${innerH * z}px` : undefined, position: 'relative', flexShrink: 0 } : { display: 'contents' }}>
      <div ref={innerRef}
           style={z ? { width: `${boxW / z}px`, transform: `scale(${z})`, transformOrigin: 'top left', containerType: 'inline-size', position: 'absolute', top: 0, left: 0 } : { display: 'contents' }}
           data-notice-zoom={z ? z.toFixed(3) : undefined}>
      {/* STUDIO-NOTICE-CARD-COMPACT-FIX-1 · free: 배지(헤더) 아래 본문 세로 배치 · 상하 여백 축소. */}
      <div style={{ display: 'flex', alignItems: 'flex-start', ...(free ? { flexDirection: 'column', justifyContent: 'flex-start' } : {}),
                    gap: free ? `clamp(${px(3)}, 1.2cqw, ${px(6)})` : 10,
                    padding: free ? `clamp(${px(5)}, 1.8cqw, ${px(9)}) clamp(${px(8)}, 3cqw, ${px(14)})` : '10px 14px', background: LIGHT.bg,
                    border: LIGHT.border, borderRadius: free ? `clamp(${px(8)}, 3.5cqw, ${px(14)})` : 14, backdropFilter: 'blur(14px)', color: LIGHT.text,
                    maxWidth: '100%', boxSizing: 'border-box', ...(free ? { width: '100%' } : {}),
                    boxShadow: '0 10px 32px rgba(0,0,0,0.35)' }} data-testid={`${testidPrefix}-overlay-notice-card`}>
        <span style={{ padding: '2px 8px', borderRadius: 999, background: 'rgba(255,59,48,0.12)', color: LIGHT.accent,
                       fontSize: free ? `clamp(${px(9)}, 3cqw, ${px(14)})` : 11, fontWeight: 900, flexShrink: 0, lineHeight: 1.2, whiteSpace: 'nowrap' }}>📢 공지</span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: free ? 6 : 8, minWidth: 0, ...(free ? { width: '100%' } : { flex: 1 }) }}>
          {text && (
            <div ref={textRef}
                 style={{ fontSize: free ? (fit ? `${fit.fs}px` : `clamp(${px(10)}, ${k}cqw, ${px(18)})`) : (isPortrait ? 14 : 12), fontWeight: 700,
                          lineHeight: free ? 1.28 : 1.35, whiteSpace: 'pre-line', overflow: 'hidden', wordBreak: 'break-word',
                          // free: 기본 block(줄바꿈 포함 실제 높이) · 최대 14줄 초과 시에만 -webkit-box 말줄임.
                          ...(free
                            ? (overflow ? { display: '-webkit-box', WebkitLineClamp: fit.clamp, WebkitBoxOrient: 'vertical' } : { display: 'block' })
                            : { display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }) }}
                 data-testid={`${testidPrefix}-overlay-notice-text`} data-autofit={free ? (fit ? fit.fs : k) : undefined} data-clamped={free && overflow ? '1' : '0'}>{text}</div>
          )}
          {showImage && notice?.image_url && (
            <img src={notice.image_url} alt="" style={{ display: 'block', maxWidth: '100%', maxHeight: maxH, objectFit: 'contain', borderRadius: 8 }}
                 onError={(e) => { try { e.currentTarget.style.display = 'none'; } catch {} }}
                 data-testid={`${testidPrefix}-overlay-notice-image`} />
          )}
        </div>
      </div>
      </div>
      </div>
    </div>
  );
};

// STEP STUDIO-OVERLAY-UX-FIX-1-STEP2 · Studio 미리보기 타이머 (EgressTemplate 타이머 스타일 동일 · 1Hz tick).
// TIMER-EDIT-END-FIX-1: 00:00 도달(finished) 시 판매자 미리보기에서 자동 숨김 · free = 배치 편집 박스 안 렌더.
export const OverlayTimerCard = ({ timer, isPortrait, viewOf, free = false, sample = false, keepFinished = false, testidPrefix = 'studio' }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!timer || timer.status !== 'running' || timer.visible === false) return undefined;
    const end = Date.parse(timer.end_at || '');
    const id = setInterval(() => {
      setNow(Date.now());
      if (!Number.isNaN(end) && Date.now() >= end) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [timer]);
  const v = viewOf(timer, now);
  // keepFinished: Egress — 기존 00:00 표시 정책 유지 (판매자 미리보기만 자동 숨김).
  if (!v || (v.status === 'finished' && !sample && !keepFinished)) return null;
  // LIVE-NOTICE-DESCRIPTION-UI-FIX-1 · 연한 반투명 (00:00 빨강은 상태 표시라 유지).
  const bg = v.status === 'finished' ? 'rgba(255,79,61,0.9)' : v.status === 'paused' ? LIGHT.bgPaused : LIGHT.bg;
  const fg = v.status === 'finished' ? '#fff' : LIGHT.text;
  // 배치 박스(free): 실제 카드 폭 기준(cqw) 반응형 — 제목 2줄 · 숫자 잘림 방지 · 여백 비례.
  if (free) {
    return (
      <div style={{ position: 'relative', pointerEvents: 'none', width: '100%', containerType: 'inline-size' }}
           data-testid={`${testidPrefix}-overlay-timer-wrap`} data-timer-status={v.status} data-sample={sample ? '1' : '0'}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'clamp(2px, 3cqw, 10px)',
                      padding: 'clamp(5px, 7cqw, 20px) clamp(6px, 8cqw, 24px)', background: bg, border: LIGHT.border,
                      borderRadius: 'clamp(8px, 10cqw, 22px)', backdropFilter: 'blur(14px)', color: fg,
                      boxShadow: LIGHT.shadow, boxSizing: 'border-box', width: '100%', textAlign: 'center' }}>
          <div style={{ fontSize: 'clamp(9px, 10cqw, 22px)', fontWeight: 900, opacity: 0.9, lineHeight: 1.2, maxWidth: '100%',
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                        wordBreak: 'keep-all', overflowWrap: 'anywhere' }}
               data-testid={`${testidPrefix}-overlay-timer-label`}>⏱ {v.label}</div>
          <div style={{ fontSize: 'clamp(14px, 24cqw, 72px)', fontWeight: 900, lineHeight: 1, fontFamily: 'monospace',
                        letterSpacing: '0.02em', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
               data-testid={`${testidPrefix}-overlay-timer-value`}>{v.display}</div>
          {v.status === 'paused' && !sample && <div style={{ fontSize: 'clamp(8px, 8cqw, 16px)', fontWeight: 800, opacity: 0.75 }} data-testid={`${testidPrefix}-overlay-timer-paused`}>⏸ 일시정지</div>}
          {sample && <div style={{ fontSize: 'clamp(8px, 8cqw, 16px)', fontWeight: 900, color: '#FFB020' }} data-testid="studio-overlay-timer-sample">미리보기 샘플</div>}
        </div>
      </div>
    );
  }
  return (
    <div style={{ position: 'absolute', pointerEvents: 'none', zIndex: 12,
                  ...(isPortrait ? { top: '36%', right: '18%' } : { top: '50%', right: '3.5%', transform: 'translateY(-50%)' }) }}
         data-testid="studio-overlay-timer-wrap" data-timer-status={v.status} data-sample={sample ? '1' : '0'}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, padding: '10px 14px', background: bg,
                    border: LIGHT.border, borderRadius: 14, backdropFilter: 'blur(14px)', color: fg,
                    boxShadow: LIGHT.shadow, minWidth: 110 }}>
        <div style={{ fontSize: 11, fontWeight: 900, opacity: 0.9, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
             data-testid="studio-overlay-timer-label">⏱ {v.label}</div>
        <div style={{ fontSize: isPortrait ? 30 : 28, fontWeight: 900, lineHeight: 1, fontFamily: 'monospace', letterSpacing: 1 }}
             data-testid="studio-overlay-timer-value">{v.display}</div>
        {v.status === 'paused' && !sample && <div style={{ fontSize: 10, fontWeight: 800, opacity: 0.75 }} data-testid="studio-overlay-timer-paused">⏸ 일시정지</div>}
        {sample && <div style={{ fontSize: 10, fontWeight: 900, color: '#FFB020' }} data-testid="studio-overlay-timer-sample">미리보기 샘플</div>}
      </div>
    </div>
  );
};

// 자유 배치용 공지 이미지 (원본 비율 유지 · object-fit contain).
export const OverlayNoticeImage = ({ src }) => (
  <img src={src} alt=""
       onError={(e) => { try { e.currentTarget.style.display = 'none'; } catch {} }}
       style={{ display: 'block', width: '100%', height: 'auto', objectFit: 'contain', borderRadius: 8,
                background: 'rgba(255,255,255,0.05)' }}
       data-testid="egress-overlay-notice-image-free" />
);
